import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './Login';
import Dashboard from './Dashboard';
import Projects from './Projects';
import ProjectDetail from './ProjectDetail';
import Reports from './Reports';
import Notifications from './Notifications';
import MyTasks from './MyTasks';
import Documentation from './Documentation';
import AccessManagement from './AccessManagement';
import GlobalCalendar from './GlobalCalendar';
import Purchases from './Purchases';
import ProfileModal from './ProfileModal';
import ThemeToggle from './ThemeToggle';
import api from './api';
import { projectCode, CAN_RESET_PASSWORD_ROLES } from './helpers';
import ayaLogo from './assets/aya-logo.png';

function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedProject, setSelectedProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [siteProgress, setSiteProgress] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // sessionStorage : la session disparait a la fermeture de l'onglet.
    const loggedInUser = sessionStorage.getItem('user');
    if (loggedInUser) {
      setUser(JSON.parse(loggedInUser));
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get('/users').then((res) => setUsers(res.data)).catch(() => setUsers([]));
    // "Avancement site" = avancement réel basé sur le % de tâches terminées (pas le budget).
    api.get('/reports/dashboard').then((res) => setSiteProgress(res.data.overallTaskProgress)).catch(() => {});
  }, [user]);

  const navigateTo = useCallback((page, project = null) => {
    setCurrentPage(page);
    if (project) setSelectedProject(project);
  }, []);

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  const handleLogout = () => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    setUser(null);
  };

  const handleProjectChanged = (updated) => {
    if (!updated) {
      navigateTo('projects');
    } else {
      setSelectedProject(updated);
    }
  };

  const NAV_ITEMS = [
    { key: 'dashboard', label: 'Tableau de bord', color: '#517997' },
    { key: 'projects', label: 'Projets', color: '#C19E54' },
    { key: 'my-tasks', label: 'Mes tâches', color: '#4CAF50' },
    { key: 'calendar', label: 'Calendrier', color: '#2E6DA4' },
    { key: 'purchases', label: 'Achats', color: '#C19E54' },
    { key: 'documentation', label: 'Documentation', color: '#8CA0B3' },
    { key: 'reports', label: 'Rapports', color: '#1B2D4D' },
  ];

  // Les directeurs administrent les comptes ; les chefs de projet accèdent à la
  // même page mais n'y voient que la réinitialisation des mots de passe.
  if (CAN_RESET_PASSWORD_ROLES.includes(user.role)) {
    NAV_ITEMS.push({ key: 'access', label: 'Accès', color: '#D9534F' });
  }

  const titleForPage = () => {
    if (currentPage === 'dashboard') return 'Vue d’ensemble du site';
    if (currentPage === 'projects') return 'Portefeuille de projets';
    if (currentPage === 'project-detail') return selectedProject?.name || '';
    if (currentPage === 'my-tasks') return 'Mes tâches';
    if (currentPage === 'calendar') return 'Calendrier des échéances';
    if (currentPage === 'purchases') return 'Suivi des demandes d’achat';
    if (currentPage === 'documentation') return 'Documentation & procédures';
    if (currentPage === 'reports') return 'Rapports & analyses';
    if (currentPage === 'access') return 'Gestion des accès';
    return '';
  };

  const breadcrumbForPage = () => {
    if (currentPage === 'dashboard') return 'PILOTAGE';
    if (currentPage === 'projects') return 'PILOTAGE / PROJETS';
    if (currentPage === 'project-detail') return 'PROJETS / ' + (selectedProject ? projectCode(selectedProject.id) : '');
    if (currentPage === 'my-tasks') return 'PILOTAGE / MES TÂCHES';
    if (currentPage === 'calendar') return 'PILOTAGE / CALENDRIER';
    if (currentPage === 'purchases') return 'PILOTAGE / ACHATS';
    if (currentPage === 'documentation') return 'PILOTAGE / DOCUMENTATION';
    if (currentPage === 'reports') return 'PILOTAGE / RAPPORTS';
    if (currentPage === 'access') return 'PILOTAGE / ACCÈS';
    return '';
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="logo">
          <img src={ayaLogo} className="logo-mark" alt="AYA Gold & Silver" />
          VIGIE
        </div>

        <div className="nav-section-label">Pilotage</div>
        <nav className="nav-menu">
          {NAV_ITEMS.map((item) => (
            <div
              key={item.key}
              className={`nav-item ${currentPage === item.key || (item.key === 'projects' && currentPage === 'project-detail') ? 'active' : ''}`}
              onClick={() => navigateTo(item.key)}
            >
              <span className="nav-dot" style={{ background: item.color }} /> {item.label}
            </div>
          ))}
          <div className="nav-divider" />
          <div className="nav-item" onClick={handleLogout}>
            <span className="nav-dot" style={{ background: '#8CA0B3' }} /> Déconnexion
          </div>
        </nav>

        {siteProgress != null && (
          <div className="sidebar-widget">
            <div className="sidebar-widget-label">Avancement site</div>
            <div className="sidebar-widget-value">{siteProgress}%</div>
            <div className="progress-bar" style={{ margin: 0 }}>
              <div className="progress-fill" style={{ width: `${siteProgress}%` }} />
            </div>
          </div>
        )}

        <div className="sidebar-user" style={{ cursor: 'pointer' }} onClick={() => setShowProfile(true)}>
          <div className="avatar avatar-lg">{user.username.substring(0, 2).toUpperCase()}</div>
          <div>
            <div className="sidebar-user-name">{user.username}</div>
            <div className="sidebar-user-role">{user.role}</div>
          </div>
        </div>
      </aside>

      {showProfile && <ProfileModal user={user} onClose={() => setShowProfile(false)} />}

      <main className="main-content">
        <header className="header">
          <div>
            <div className="breadcrumb">{breadcrumbForPage()}</div>
            <h1 className="page-title">{titleForPage()}</h1>
          </div>
          <div className="header-right">
            <input
              className="search-input"
              placeholder="Rechercher un projet..."
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
                if (value.trim() && currentPage !== 'dashboard' && currentPage !== 'projects') {
                  navigateTo('projects');
                }
              }}
            />
            <ThemeToggle compact />
            <Notifications userId={user.id} />
            <button className="btn btn-sm" onClick={handleLogout}>Quitter</button>
          </div>
        </header>

        <section className="content">
          {currentPage === 'dashboard' && (
            <Dashboard onSelectProject={(p) => navigateTo('project-detail', p)} searchQuery={searchQuery} />
          )}
          {currentPage === 'projects' && (
            <Projects onSelectProject={(p) => navigateTo('project-detail', p)} users={users} searchQuery={searchQuery} />
          )}
          {currentPage === 'project-detail' && selectedProject && (
            <ProjectDetail
              project={selectedProject}
              users={users}
              currentUser={user}
              onBack={() => navigateTo('projects')}
              onProjectChanged={handleProjectChanged}
            />
          )}
          {currentPage === 'my-tasks' && (
            <MyTasks users={users} currentUser={user} onOpenProject={(p) => navigateTo('project-detail', p)} />
          )}
          {currentPage === 'calendar' && (
            <GlobalCalendar users={users} currentUser={user} onOpenProject={(p) => navigateTo('project-detail', p)} />
          )}
          {currentPage === 'purchases' && (
            <Purchases users={users} currentUser={user} onOpenProject={(p) => navigateTo('project-detail', p)} />
          )}
          {currentPage === 'documentation' && <Documentation users={users} currentUser={user} />}
          {currentPage === 'reports' && <Reports />}
          {currentPage === 'access' && CAN_RESET_PASSWORD_ROLES.includes(user.role) && (
            <AccessManagement currentUser={user} />
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
